import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flag, Plus, Tags, Trash2, Users } from 'lucide-react';

import { EntitySelect } from '@/components/EntitySelect';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { SaveButton } from '@/components/ui/save-button';
import { Switch } from '@/components/ui/switch';
import { useSavedFlash } from '@/hooks/useSavedFlash';
import { cn } from '@/lib/utils';
import {
  ESTIMATE_TYPES,
  MILESTONE_STATUSES,
  PRIORITIES,
  PROJECT_MEMBER_ROLES,
  PROJECT_STATUSES,
  PROJECT_VISIBILITIES,
  STATE_GROUPS,
} from '../constants';
import {
  labelsHooks,
  projectMembersHooks,
  statesHooks,
  useArchiveProject,
  useCreateMilestoneStandalone,
  useDeleteMilestone,
  useDeleteProject,
  useMilestones,
  useRestoreProject,
  useUpdateMilestone,
  useUpdateProject,
  workItemTypesHooks,
} from '../queries';
import { projectMemberName, resolveMemberByIdLabel, searchMembersById } from '../select';
import { reorderRows } from '../reorder';
import { ReorderButtons } from './ReorderButtons';
import type {
  EstimateType,
  Label as LabelModel,
  Milestone,
  MilestoneStatus,
  Priority,
  Project,
  ProjectMember,
  ProjectMemberRole,
  ProjectStatus,
  ProjectVisibility,
  State,
  StateGroup,
  WorkItemType,
} from '../types';

const SUBTABS = ['General', 'Members', 'States', 'Labels', 'Types', 'Milestones'] as const;
type SubTab = (typeof SUBTABS)[number];

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

export function ProjectSettingsTab({ project }: { project: Project }) {
  const [sub, setSub] = useState<SubTab>('General');
  return (
    <div className="max-w-3xl space-y-5">
      <div className="flex flex-wrap gap-1">
        {SUBTABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSub(s)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium',
              sub === s ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {s}
          </button>
        ))}
      </div>

      {sub === 'General' ? (
        <GeneralSettings project={project} />
      ) : sub === 'Members' ? (
        <MembersSettings project={project} />
      ) : sub === 'States' ? (
        <StatesSettings project={project} />
      ) : sub === 'Labels' ? (
        <LabelsSettings project={project} />
      ) : sub === 'Types' ? (
        <TypesSettings project={project} />
      ) : (
        <MilestonesSettings project={project} />
      )}
    </div>
  );
}

// IANA timezone list straight from the runtime (no extra dependency). Guarded
// for older runtimes that lack `Intl.supportedValuesOf`.
const TZ_SUPPORTED = (Intl as typeof Intl & { supportedValuesOf?: (k: string) => string[] })
  .supportedValuesOf;
const TIMEZONES: string[] = TZ_SUPPORTED ? TZ_SUPPORTED('timeZone') : [];
const tzLabel = (tz: string) => tz.replace(/_/g, ' ');

async function searchTimezones(query: string) {
  const q = query.trim().toLowerCase();
  const matches = q ? TIMEZONES.filter((tz) => tz.toLowerCase().includes(q)) : TIMEZONES;
  return matches.slice(0, 50).map((tz) => ({ value: tz, label: tzLabel(tz) }));
}

async function resolveTimezone(value: string) {
  return value ? tzLabel(value) : null;
}

function GeneralSettings({ project }: { project: Project }) {
  const navigate = useNavigate();
  const update = useUpdateProject(project.public_id, { notify: false });
  const { saved, flash, clear: clearSaved } = useSavedFlash();
  const archive = useArchiveProject();
  const restore = useRestoreProject();
  const del = useDeleteProject();

  const [form, setForm] = useState({
    name: project.name,
    identifier: project.identifier,
    icon: project.icon,
    visibility: project.visibility,
    status: project.status,
    priority: project.priority,
    estimate_type: project.estimate_type,
    timezone: project.timezone,
    description: project.description,
    kb_sync_enabled: project.kb_sync_enabled,
  });

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    clearSaved();
  }

  // Send the KB switch only when it was flipped in this form: a stale `false`
  // riding along with an unrelated save would purge the project's KB (DEV13-113).
  const [kbTouched, setKbTouched] = useState(false);
  function save() {
    const input: Partial<typeof form> = { ...form };
    if (!kbTouched) delete input.kb_sync_enabled;
    update.mutate(input, {
      onSuccess: () => {
        setKbTouched(false);
        flash();
      },
    });
  }

  return (
    <div className="max-w-4xl space-y-6">
      <FormSection
        title="Identity"
        description="How the project is named and referenced across the workspace."
      >
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Project ID</Label>
          <Input
            value={form.identifier}
            maxLength={10}
            onChange={(e) => set('identifier', e.target.value.toUpperCase())}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Icon (emoji)</Label>
          <EmojiPicker value={form.icon} onChange={(v) => set('icon', v)} />
        </div>
      </FormSection>

      <FormSection
        title="Classification"
        description="Visibility, lifecycle, and planning settings."
        columns={4}
      >
        <div className="space-y-1.5">
          <Label>Access</Label>
          <Select value={form.visibility} onValueChange={(v) => set('visibility', v as ProjectVisibility)}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROJECT_VISIBILITIES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => set('status', v as ProjectStatus)}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROJECT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Priority</Label>
          <Select value={form.priority} onValueChange={(v) => set('priority', v as Priority)}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Estimate type</Label>
          <Select value={form.estimate_type} onValueChange={(v) => set('estimate_type', v as EstimateType)}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ESTIMATE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
          <Label>Timezone</Label>
          <EntitySelect
            queryKey="project-timezone"
            value={form.timezone}
            onChange={(v) => set('timezone', v)}
            search={searchTimezones}
            resolveLabel={resolveTimezone}
            placeholder="Select a timezone…"
            searchPlaceholder="Search timezones…"
            clearable
          />
        </div>
      </FormSection>

      <FormSection title="Description" description="Shown on the project Overview." columns={1}>
        <RichTextEditor
          value={form.description}
          placeholder="Add a project description…"
          onChange={(html) => set('description', html)}
        />
      </FormSection>

      <FormSection
        title="Knowledge Base"
        description="Lets this project's pages and files be synced to the Knowledge Base the AI assistant answers from."
        columns={1}
      >
        <div className="flex items-start gap-3">
          <Switch
            id="kb-sync-enabled"
            checked={form.kb_sync_enabled}
            onCheckedChange={(v) => {
              set('kb_sync_enabled', v);
              setKbTouched(true);
            }}
          />
          <div className="space-y-0.5">
            <Label htmlFor="kb-sync-enabled">Sync to Knowledge Base</Label>
            <p className="text-muted-foreground text-xs">
              {form.kb_sync_enabled
                ? 'Sync buttons appear on the Pages tab.'
                : project.kb_sync_enabled
                  ? "Saving removes this project's pages and files from the Knowledge Base and hides the sync buttons."
                  : 'Off — sync buttons are hidden. Turning it back on does not re-sync by itself.'}
            </p>
          </div>
        </div>
      </FormSection>

      <div className="flex justify-end">
        <SaveButton pending={update.isPending} saved={saved} onClick={save}>
          Update project
        </SaveButton>
      </div>

      <div className="space-y-3 pt-2">
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">Danger zone</h3>
        <div className="flex items-center justify-between rounded-md border p-4">
          <div>
            <p className="font-medium">Archive</p>
            <p className="text-muted-foreground text-sm">
              Unlists the project from the side nav; still reachable from the projects page.
            </p>
          </div>
          {project.is_archived ? (
            <Button variant="outline" onClick={() => restore.mutate(project.public_id)}>Restore</Button>
          ) : (
            <Button variant="outline" onClick={() => archive.mutate(project.public_id)}>Archive</Button>
          )}
        </div>

        <div className="border-destructive/40 flex items-center justify-between rounded-md border p-4">
          <div>
            <p className="font-medium">Delete</p>
            <p className="text-muted-foreground text-sm">Permanently delete this project and its work items.</p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete “{project.name}”?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the project, its work items, states, modules and cycles. This
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => del.mutate(project.public_id, { onSuccess: () => navigate('/projects') })}
                >
                  Delete project
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

/** Dashed empty-state card used across the settings list tabs. */
function EmptyState({
  icon: Icon,
  title,
  hint,
}: {
  icon: typeof Users;
  title: string;
  hint: string;
}) {
  return (
    <div className="rounded-md border border-dashed p-6 text-center">
      <Icon className="text-muted-foreground/40 mx-auto size-8" />
      <p className="mt-2 text-sm font-medium">{title}</p>
      <p className="text-muted-foreground text-sm">{hint}</p>
    </div>
  );
}

/** Titled card grouping related General-settings fields. */
function FormSection({
  title,
  description,
  columns = 2,
  children,
}: {
  title: string;
  description?: string;
  columns?: 1 | 2 | 4;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border">
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
      </div>
      <div
        className={cn(
          'grid grid-cols-1 gap-4 p-4',
          columns === 2 && 'sm:grid-cols-2',
          columns === 4 && 'sm:grid-cols-2 lg:grid-cols-4',
        )}
      >
        {children}
      </div>
    </section>
  );
}

function MembersSettings({ project }: { project: Project }) {
  const query = projectMembersHooks.useList({ project: project.id });
  const create = projectMembersHooks.useCreate();
  const update = projectMembersHooks.useUpdate();
  const remove = projectMembersHooks.useRemove();
  const [member, setMember] = useState('');
  const [role, setRole] = useState<ProjectMemberRole>('member');

  const members = useMemo(() => rows<ProjectMember>(query.data), [query.data]);
  const isPublic = project.visibility === 'public';
  const alreadyMember = member !== '' && members.some((m) => String(m.member) === member);

  function add() {
    if (!member || alreadyMember) return;
    create.mutate({ project: project.id, member: Number(member), role }, { onSuccess: () => setMember('') });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1 space-y-1">
          <Label className="text-xs">Member</Label>
          <EntitySelect
            queryKey="project-member-picker"
            value={member}
            onChange={setMember}
            search={searchMembersById}
            resolveLabel={resolveMemberByIdLabel}
            placeholder="Select a member"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Role</Label>
          <Select value={role} onValueChange={(v) => setRole(v as ProjectMemberRole)}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROJECT_MEMBER_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={add} disabled={!member || alreadyMember || create.isPending}>
          <Plus className="size-4" /> Add
        </Button>
      </div>
      {alreadyMember ? (
        <p className="text-muted-foreground -mt-2 text-xs">That member is already on the project.</p>
      ) : null}

      {/* Visibility context — the access rules differ for public vs private. */}
      <p className="text-muted-foreground text-xs">
        {isPublic
          ? 'This project is public — every staff member can access it. Adding members here assigns roles (e.g. Lead).'
          : 'This project is private — only the members below, the project lead, and admins can access it.'}
      </p>
      <p className="text-muted-foreground text-xs">
        Adding a <span className="font-medium">client (guest)</span> here grants them scoped access to
        this project — <span className="font-medium">Viewer</span> is read-only;{' '}
        <span className="font-medium">Member</span>/<span className="font-medium">Lead</span> can also
        create and edit tasks and pages.
      </p>

      {members.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No members yet"
          hint={
            isPublic
              ? 'Add members to give them an explicit role on this project.'
              : 'Add members so they can see and work on this private project.'
          }
        />
      ) : (
        <ul className="divide-y rounded-md border">
          {members.map((m) => (
            <li key={m.id} className="flex items-center gap-3 px-3 py-2">
              <span className="bg-muted text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium">
                {(m.member_name ?? '?').charAt(0).toUpperCase()}
              </span>
              <span className="flex-1 truncate text-sm">{projectMemberName(m)}</span>
              <Select
                value={m.role}
                onValueChange={(v) => update.mutate({ id: m.id, input: { role: v as ProjectMemberRole } })}
              >
                <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROJECT_MEMBER_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                variant="ghost" size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => remove.mutate(m.id)}
                aria-label={`Remove ${m.member_name ?? 'member'}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatesSettings({ project }: { project: Project }) {
  const query = statesHooks.useList({ project: project.id });
  const create = statesHooks.useCreate();
  const update = statesHooks.useUpdate();
  const remove = statesHooks.useRemove();
  const [name, setName] = useState('');
  const [group, setGroup] = useState<StateGroup>('unstarted');
  const [color, setColor] = useState('#64748b');

  const states = useMemo(
    () => rows<State>(query.data).sort((a, b) => a.order - b.order),
    [query.data],
  );

  const move = (index: number, dir: -1 | 1) =>
    reorderRows(states, index, dir, (state, order) => update.mutate({ id: state.id, input: { order } }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 space-y-1"><Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Discovery" />
        </div>
        <div className="space-y-1"><Label className="text-xs">Group</Label>
          <Select value={group} onValueChange={(v) => setGroup(v as StateGroup)}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATE_GROUPS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Color</Label>
          <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-14 p-1" />
        </div>
        <Button
          onClick={() => {
            if (name.trim()) {
              create.mutate({ project: project.id, name: name.trim(), group, color, order: states.length });
              setName('');
            }
          }}
          disabled={!name.trim() || create.isPending}
        >
          <Plus className="size-4" /> Add
        </Button>
      </div>
      <ul className="space-y-2">
        {states.map((s, i) => (
          <li key={s.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
            <ReorderButtons index={i} count={states.length} onMove={(dir) => move(i, dir)} />
            <span className="size-3 rounded-full" style={{ backgroundColor: s.color || '#94a3b8' }} />
            <span className="flex-1 text-sm">{s.name}</span>
            <Badge variant="outline" className="text-xs">{s.group}</Badge>
            {s.is_default ? <Badge className="text-xs">default</Badge> : null}
            <Button
              variant="ghost" size="icon"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => remove.mutate(s.id)}
              aria-label={`Delete ${s.name}`}
            >
              <Trash2 className="size-4" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LabelsSettings({ project }: { project: Project }) {
  const query = labelsHooks.useList({ project: project.id });
  const create = labelsHooks.useCreate();
  const remove = labelsHooks.useRemove();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6366f1');
  const labels = useMemo(() => rows<LabelModel>(query.data), [query.data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 space-y-1"><Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="bug" />
        </div>
        <div className="space-y-1"><Label className="text-xs">Color</Label>
          <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-14 p-1" />
        </div>
        <Button
          onClick={() => {
            if (name.trim()) {
              create.mutate({ project: project.id, name: name.trim(), color });
              setName('');
            }
          }}
          disabled={!name.trim() || create.isPending}
        >
          <Plus className="size-4" /> Add
        </Button>
      </div>
      {query.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : labels.length === 0 ? (
        <EmptyState
          icon={Tags}
          title="No labels yet"
          hint="Create labels to categorise and filter work items (e.g. bug, frontend, urgent)."
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {labels.map((l) => (
            <span
              key={l.id}
              className="bg-muted/40 flex items-center gap-1.5 rounded-full border py-1 pr-1.5 pl-2.5 text-sm"
            >
              <span className="size-2.5 rounded-full" style={{ backgroundColor: l.color || '#6366f1' }} />
              {l.name}
              <button
                type="button"
                onClick={() => remove.mutate(l.id)}
                className="text-muted-foreground hover:bg-background hover:text-destructive rounded-full p-0.5"
                aria-label={`Delete ${l.name}`}
              >
                <Trash2 className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function TypesSettings({ project }: { project: Project }) {
  const query = workItemTypesHooks.useList({ project: project.id });
  const create = workItemTypesHooks.useCreate();
  const update = workItemTypesHooks.useUpdate();
  const remove = workItemTypesHooks.useRemove();
  const [name, setName] = useState('');
  const types = useMemo(
    () => rows<WorkItemType>(query.data).sort((a, b) => a.order - b.order),
    [query.data],
  );

  const move = (index: number, dir: -1 | 1) =>
    reorderRows(types, index, dir, (t, order) => update.mutate({ id: t.id, input: { order } }));

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New type (e.g. Spike)" />
        <Button
          onClick={() => {
            if (name.trim()) {
              create.mutate({ project: project.id, name: name.trim(), order: types.length });
              setName('');
            }
          }}
          disabled={!name.trim() || create.isPending}
        >
          <Plus className="size-4" /> Add
        </Button>
      </div>
      <ul className="space-y-2">
        {types.map((t, i) => (
          <li key={t.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
            <ReorderButtons index={i} count={types.length} onMove={(dir) => move(i, dir)} />
            <span className="flex-1 text-sm">{t.name}</span>
            {t.is_epic ? <Badge variant="outline" className="text-xs">epic</Badge> : null}
            {t.is_default ? <Badge className="text-xs">default</Badge> : null}
            {!t.is_active ? <Badge variant="secondary" className="text-xs">inactive</Badge> : null}
            <Button
              variant="ghost" size="icon"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => remove.mutate(t.id)}
              aria-label={`Delete ${t.name}`}
            >
              <Trash2 className="size-4" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MilestonesSettings({ project }: { project: Project }) {
  const query = useMilestones({ project: project.id });
  const create = useCreateMilestoneStandalone();
  const update = useUpdateMilestone();
  const remove = useDeleteMilestone();
  const [name, setName] = useState('');
  const [due, setDue] = useState('');

  const milestones = useMemo(() => rows<Milestone>(query.data), [query.data]);

  function add() {
    if (!name.trim()) return;
    create.mutate(
      { project: project.id, name: name.trim(), due_date: due || null, status: 'open' },
      { onSuccess: () => { setName(''); setDue(''); } },
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        Dated deliverables / gates for this project. Work items can be tagged to a milestone.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="MVP delivered" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Due date</Label>
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-44" />
        </div>
        <Button onClick={add} disabled={!name.trim() || create.isPending}>
          <Plus className="size-4" /> Add
        </Button>
      </div>
      {query.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : milestones.length === 0 ? (
        <EmptyState
          icon={Flag}
          title="No milestones yet"
          hint="Add dated gates like “MVP delivered” or “Beta launch”, then tag work items to them."
        />
      ) : (
        <ul className="space-y-2">
          {milestones.map((m) => (
            <li key={m.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
              <span className="flex-1 truncate text-sm">{m.name}</span>
              <span className="text-muted-foreground text-xs">{m.due_date ?? '—'}</span>
              <Select
                value={m.status}
                onValueChange={(v) => update.mutate({ id: m.id, input: { status: v as MilestoneStatus } })}
              >
                <SelectTrigger className="h-8 w-28">
                  <SelectValue>
                    <Badge variant={m.status === 'done' ? 'default' : 'outline'}>
                      {MILESTONE_STATUSES.find((s) => s.value === m.status)?.label ?? m.status}
                    </Badge>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {MILESTONE_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost" size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => remove.mutate({ id: m.id, projectId: project.id })}
                aria-label={`Delete ${m.name}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
